// ─── Judging a photograph before it is worth uploading ───────────────────────
//
// The cheapest accuracy in the whole plate-reading pipeline, and the part that
// costs nothing to run: a blurred frame rejected on the phone never becomes a
// failed reading, a wasted upload over a bad yard connection, or a driver
// staring at a spinner to be told to try again.
//
// Everything here is arithmetic over pixel data. No model, no network, no
// canvas ownership — the caller supplies `ImageData` and gets a number back —
// which is what makes it testable without a browser.

/**
 * How sharp a frame has to be before it is worth sending.
 *
 * The variance of the Laplacian: run an edge detector over the greyscale image
 * and measure how much its output varies. A sharp photograph has strong edges
 * in some places and none in others, so the variance is high; a blurred one has
 * soft gradients everywhere and the variance collapses. It is the standard
 * cheap sharpness measure and it costs one pass over the pixels.
 *
 * The threshold is deliberately forgiving. A container door is a low-detail
 * subject — flat paint, a few stencilled characters — so it scores lower than
 * an ordinary photograph of the same sharpness, and a strict cut-off would
 * reject frames the recogniser reads perfectly well. Rejecting a good frame
 * costs a driver a retake for nothing, which is exactly the friction this is
 * supposed to remove.
 */
export const SHARPNESS_FLOOR = 55;

/**
 * The smallest frame worth reading, on its long edge.
 *
 * Below this the stencil is not resolvable no matter how sharp the image is.
 * Well under what any current phone produces; it exists to catch a thumbnail
 * or a downscale that went wrong rather than to judge a camera.
 */
export const MIN_LONG_EDGE = 640;

/** What the reader is sent, on the long edge. */
export const UPLOAD_LONG_EDGE = 1600;

/** JPEG quality for the upload. High enough that stencil edges survive it. */
export const UPLOAD_QUALITY = 0.92;

/** How many frames a burst takes. */
export const BURST_FRAMES = 1;

/**
 * The most frames that will ever be sent in one reading.
 *
 * Matches `PLATE_FRAME_MAX_FILES` on the API, and exists because the picker can
 * hand back an arbitrary number of files while a burst never can. Selecting
 * twelve photographs and having the upload refused by multer with a message
 * about field counts is a worse answer than quietly reading the best five.
 */
export const MAX_UPLOAD_FRAMES = 1;

/** Milliseconds between frames of a burst. */
export const BURST_INTERVAL_MS = 220;

/**
 * Why a frame was rejected, when it was.
 *
 * Separate reasons rather than one "bad photo", because they call for different
 * things: move closer, hold still, turn a light on. A driver told only that a
 * photograph was no good will take the same one again.
 */
export type FrameProblem = "tooSmall" | "blurred" | "tooDark";

export interface FrameQuality {
  ok: boolean;
  problem?: FrameProblem;
  /** Laplacian variance — higher is sharper. */
  sharpness: number;
  /** Mean luminance, 0–255. */
  brightness: number;
}

/**
 * Greyscale luminance, at the weights the eye actually uses.
 *
 * Not a plain average of the channels: green carries most of perceived
 * brightness and blue almost none, and on a blue or red container — which is
 * most of them — a flat average throws away contrast that the stencil's edges
 * live in.
 */
function luminance(data: Uint8ClampedArray, index: number): number {
  return (
    0.2126 * data[index] + 0.7152 * data[index + 1] + 0.0722 * data[index + 2]
  );
}

/**
 * How sharp an image is, and how bright.
 *
 * One pass, computing both, because both are wanted on every frame and the
 * expensive part is walking the pixels rather than the arithmetic at each one.
 *
 * The Laplacian used is the four-neighbour kernel — up, down, left, right,
 * minus four times the centre. The border ring is skipped rather than clamped:
 * a clamped edge invents a discontinuity all the way round the frame, which
 * adds the same spurious variance to a sharp image and a blurred one and
 * flattens exactly the difference being measured.
 */
export function measureFrame(image: ImageData): FrameQuality {
  const { data, width, height } = image;

  if (Math.max(width, height) < MIN_LONG_EDGE) {
    return { ok: false, problem: "tooSmall", sharpness: 0, brightness: 0 };
  }

  let sum = 0;
  let sumSquares = 0;
  let brightnessSum = 0;
  let count = 0;

  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const i = (y * width + x) * 4;
      const centre = luminance(data, i);

      const laplacian =
        luminance(data, i - 4) +
        luminance(data, i + 4) +
        luminance(data, i - width * 4) +
        luminance(data, i + width * 4) -
        4 * centre;

      sum += laplacian;
      sumSquares += laplacian * laplacian;
      brightnessSum += centre;
      count += 1;
    }
  }

  if (count === 0) {
    return { ok: false, problem: "tooSmall", sharpness: 0, brightness: 0 };
  }

  const mean = sum / count;
  const sharpness = sumSquares / count - mean * mean;
  const brightness = brightnessSum / count;

  // Darkness is checked before blur, and the order is the whole point of
  // separating them: an underexposed frame is *also* blurred by this measure,
  // because there is no contrast for edges to be found in. Reporting it as blur
  // would send the driver to hold the phone steadier when what they need is a
  // light — advice that cannot work, given until they give up.
  if (brightness < 35) {
    return { ok: false, problem: "tooDark", sharpness, brightness };
  }
  if (sharpness < SHARPNESS_FLOOR) {
    return { ok: false, problem: "blurred", sharpness, brightness };
  }

  return { ok: true, sharpness, brightness };
}

/**
 * Which frames of a burst are worth uploading.
 *
 * Sharpest first, so if the reader only looks at some of them it looks at the
 * best. Frames that fail the gate are dropped — but never all of them: if
 * nothing passed, the single best frame goes anyway.
 *
 * That fallback matters more than it looks. The gate is a heuristic tuned on a
 * low-detail subject, and a recogniser is often perfectly capable of reading a
 * frame this rejects. Refusing to upload anything would turn a heuristic's
 * opinion into a hard block and leave a driver in poor light unable to use the
 * feature at all — with no way to overrule it. Sending the best of a bad set
 * costs one request and may well work.
 */
export function selectBurstFrames<T>(
  frames: Array<{ frame: T; quality: FrameQuality }>,
): { selected: T[]; rejected: FrameProblem[] } {
  const ranked = [...frames].sort(
    (a, b) => b.quality.sharpness - a.quality.sharpness,
  );

  const passed = ranked.filter((f) => f.quality.ok);
  const rejected = ranked
    .filter((f) => !f.quality.ok)
    .map((f) => f.quality.problem)
    .filter((p): p is FrameProblem => p !== undefined);

  if (passed.length) {
    return { selected: passed.map((f) => f.frame), rejected };
  }

  return {
    selected: ranked.length ? [ranked[0].frame] : [],
    rejected,
  };
}

/**
 * The advice a rejected burst deserves.
 *
 * The commonest problem across the burst, not the first — one dark frame in
 * four taken while a lorry passed is not a lighting problem, and naming it as
 * one sends the driver after the wrong fix.
 */
export function dominantProblem(
  problems: FrameProblem[],
): FrameProblem | null {
  if (!problems.length) return null;
  const counts = new Map<FrameProblem, number>();
  for (const problem of problems) {
    counts.set(problem, (counts.get(problem) ?? 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0];
}

/**
 * The dimensions to send, preserving the aspect ratio.
 *
 * Downscaling here rather than leaving it to the service is about the upload,
 * not about recognition: a driver in a yard is often on a poor connection, and
 * a full-resolution frame is several megabytes of it. The reader resizes to
 * roughly this anyway, so nothing legible is lost.
 *
 * An image already smaller than the target is left alone. Upscaling would add
 * bytes and no detail.
 */
/**
 * Draw `source` into `context` at a smaller size, without throwing detail away.
 *
 * `drawImage` scaled down in one step is what a canvas does by default, and for
 * a photograph of a door plate it is the wrong thing. The browser's filter for
 * that path samples too few pixels, so on any reduction past about 2x thin
 * strokes do not soften — they *vanish*. The boxed check digit is the smallest,
 * thinnest glyph on the whole container, and it was disappearing on the phone
 * before the photograph was ever uploaded: the reader then had ten characters
 * where the door has eleven, and computed the one it could no longer see.
 *
 * `createImageBitmap` with `resizeQuality: "high"` does a proper job and is
 * hardware-accelerated where it exists. Where it does not — or where the
 * options are ignored, which some browsers do silently — the fallback halves
 * repeatedly instead. Each halving is a 2x reduction, which is exactly the
 * ratio the default filter handles acceptably, so the same pixels survive by a
 * slower road.
 */
export async function drawDownscaled(
  context: CanvasRenderingContext2D,
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
  width: number,
  height: number,
): Promise<void> {
  if (sourceWidth <= width && sourceHeight <= height) {
    context.drawImage(source, 0, 0, width, height);
    return;
  }

  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(source, {
        resizeWidth: width,
        resizeHeight: height,
        resizeQuality: "high",
      });
      // Browsers that do not implement the resize options ignore them and hand
      // back the original size rather than failing, so the result is checked
      // instead of the feature.
      if (bitmap.width === width && bitmap.height === height) {
        context.drawImage(bitmap, 0, 0);
        bitmap.close?.();
        return;
      }
      bitmap.close?.();
    } catch {
      // Tainted canvas, an unsupported source type, or an older engine. The
      // halving below needs none of it.
    }
  }

  let current: CanvasImageSource = source;
  let currentWidth = sourceWidth;
  let currentHeight = sourceHeight;

  while (currentWidth > width * 2 && currentHeight > height * 2) {
    const nextWidth = Math.max(width, Math.round(currentWidth / 2));
    const nextHeight = Math.max(height, Math.round(currentHeight / 2));

    const step = document.createElement("canvas");
    step.width = nextWidth;
    step.height = nextHeight;
    const stepContext = step.getContext("2d");
    if (!stepContext) break;
    stepContext.drawImage(current, 0, 0, nextWidth, nextHeight);

    current = step;
    currentWidth = nextWidth;
    currentHeight = nextHeight;
  }

  context.drawImage(current, 0, 0, width, height);
}

export function uploadSize(
  width: number,
  height: number,
  longEdge: number = UPLOAD_LONG_EDGE,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= longEdge || longest === 0) {
    return { width, height };
  }
  const scale = longEdge / longest;
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  };
}


// ─── Frames that arrive as files ─────────────────────────────────────────────
//
// The picker path, which exists for two quite different reasons.
//
// The first is development: the live camera needs a secure context and a rear
// camera, and neither is a given on the machine this is built on. Being able to
// feed the pipeline a folder of container photographs is the difference between
// testing the whole feature and testing the half of it that runs on a server.
//
// The second matters in production. A camera can be unavailable for reasons the
// driver cannot do anything about — a permission denied at the device policy
// level, a browser that will not grant it over a plain-HTTP intranet, a cracked
// lens. Those drivers had exactly one fallback, which was to type everything by
// hand; now they can photograph the door with the phone's own camera app and
// pick the result.
//
// Deliberately the *same* pipeline. Uploaded frames are measured, ranked and
// downscaled exactly as captured ones are, so a blurred photograph is caught
// either way and dev testing exercises the code that actually ships.

/**
 * Decode a picked file onto a canvas, downscaled to what will be uploaded.
 *
 * `createImageBitmap` is preferred because it applies the EXIF orientation for
 * us. Phones record rotation as metadata rather than rotating the pixels, so a
 * portrait photograph decoded naively arrives on its side — and a plate read
 * sideways yields confident nonsense rather than an obvious failure. The
 * `<img>` fallback is for older Safari, which honours EXIF when drawing anyway.
 */
async function decodeToCanvas(
  file: File,
): Promise<{ canvas: HTMLCanvasElement; image: ImageData } | null> {
  let width = 0;
  let height = 0;
  let source: CanvasImageSource;

  if (typeof createImageBitmap === "function") {
    const bitmap = await createImageBitmap(file, {
      imageOrientation: "from-image",
    });
    source = bitmap;
    width = bitmap.width;
    height = bitmap.height;
  } else {
    const element = document.createElement("img");
    const url = URL.createObjectURL(file);
    try {
      await new Promise<void>((resolve, reject) => {
        element.onload = () => resolve();
        element.onerror = () => reject(new Error("decode failed"));
        element.src = url;
      });
    } finally {
      URL.revokeObjectURL(url);
    }
    source = element;
    width = element.naturalWidth;
    height = element.naturalHeight;
  }

  if (!width || !height) return null;

  // Measured at the size it will be sent at, which is also the size the reader
  // resizes to. Measuring at full resolution instead would score a 12-megapixel
  // frame on a scale the captured frames are never judged on, and the threshold
  // would then mean two different things depending on which button was pressed.
  const target = uploadSize(width, height);
  const canvas = document.createElement("canvas");
  canvas.width = target.width;
  canvas.height = target.height;

  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) return null;
  await drawDownscaled(context, source, width, height, target.width, target.height);

  return {
    canvas,
    image: context.getImageData(0, 0, target.width, target.height),
  };
}

/**
 * Turn picked files into frames ready to upload, measured like any others.
 *
 * Files that cannot be decoded at all are dropped rather than failing the set —
 * a picker will happily hand back a HEIC that this browser has no decoder for,
 * and one unusable file among five should cost that file and nothing else.
 *
 * Returns at most `MAX_UPLOAD_FRAMES`, sharpest first.
 */
export async function prepareUploadedFrames(
  files: File[],
): Promise<{ selected: File[]; rejected: FrameProblem[]; undecodable: number }> {
  const measured: Array<{ frame: File; quality: FrameQuality }> = [];
  let undecodable = 0;

  for (const file of files.slice(0, MAX_UPLOAD_FRAMES * 2)) {
    let decoded: Awaited<ReturnType<typeof decodeToCanvas>> = null;
    try {
      decoded = await decodeToCanvas(file);
    } catch {
      decoded = null;
    }
    if (!decoded) {
      undecodable += 1;
      continue;
    }

    const quality = measureFrame(decoded.image);
    const blob = await new Promise<Blob | null>((resolve) =>
      decoded!.canvas.toBlob(resolve, "image/jpeg", UPLOAD_QUALITY),
    );
    if (!blob) {
      undecodable += 1;
      continue;
    }

    measured.push({
      frame: new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", {
        type: "image/jpeg",
      }),
      quality,
    });
  }

  const { selected, rejected } = selectBurstFrames(measured);
  return {
    selected: selected.slice(0, MAX_UPLOAD_FRAMES),
    rejected,
    undecodable,
  };
}
